// FE-SCRM-055 — React Query hooks for the Settings page.
//
// Tag inventory + rename/merge/delete mutations invalidate the same
// query key so the table refreshes in place. Broker-settings
// mutations reuse the existing ``usePatchBrokerSettingsMutation`` so
// the entity-scoped JSON blob stays the single source of truth for
// per-broker flags.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { deleteTag, fetchTagInventory, mergeTag, renameTag } from "./api";
import type { TagInventoryResponse } from "./types";


export const TAG_INVENTORY_QUERY_KEY = "sales-tag-inventory" as const;


export function useTagInventoryQuery(): UseQueryResult<TagInventoryResponse> {
  return useQuery({
    queryKey: [TAG_INVENTORY_QUERY_KEY],
    queryFn: ({ signal }) => fetchTagInventory({ signal }),
  });
}


export function useRenameTagMutation(): UseMutationResult<
  { tag: string; slug: string },
  Error,
  { from_tag: string; to_tag: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => renameTag(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TAG_INVENTORY_QUERY_KEY] });
    },
  });
}


export function useMergeTagMutation(): UseMutationResult<
  { tag: string; slug: string },
  Error,
  { from_tag: string; into_tag: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => mergeTag(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TAG_INVENTORY_QUERY_KEY] });
    },
  });
}


export function useDeleteTagMutation(): UseMutationResult<
  { tag: string; slug: string },
  Error,
  { tag: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => deleteTag(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [TAG_INVENTORY_QUERY_KEY] });
    },
  });
}
